import React, { useEffect } from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import { FormikValues, useFormik } from 'formik';
import BackIcon from '@mui/icons-material/ArrowBack';
import {
  GA_EVENTS,
  Language,
  SignUpFlowScreens,
  createProfessionalProfile,
  defaultPrefferedRange,
  getLanguages,
  getProfileUtils,
  getUserProps,
  istermsAgreedStatus,
  userLocation,
} from '@pharmaplan/common';

import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import CustomButton from '../../common/CustomButton';
import ProfileForm from '../../Profile/ProfileForm';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import strings from '../../../localization';
import { professionalPersist } from '../../../selectors/formPersistSelector';
import { filterLanguage } from '../../../helpers/Functions';

import { professionalSteps } from '../helper';

import classes from './styles';
import useSignupErrors from '../../../hooks/useSignupFlow/useSignupErrors';
import useAnalytics from '../../../analytics/useAnalytics';

const { capacityId } = idStore ?? {};
interface IPharmacistSignupFlow {
  step: number;
  changeStep: (steps: object, values: FormikValues, type: string) => void;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const PharmacistSignupFlow = ({
  step,
  setStep,
  changeStep,
}: IPharmacistSignupFlow) => {
  const dispatch = useAppDispatch();

  const professionalPersistSelector = useAppSelector(professionalPersist);
  const isTerms = useAppSelector(istermsAgreedStatus);
  const location = useAppSelector(userLocation);
  const languages = useAppSelector(getLanguages);
  const userProps = useAppSelector(getUserProps);
  const pharmacistStep = professionalSteps()?.[step as keyof typeof professionalSteps];
  const isLastStep = step === Object.keys(professionalSteps()).length - 1;
  const validStep = step !== 0;
  const proUtils = useAppSelector(getProfileUtils);
  const { softwares } = proUtils ?? {};
  const { back, backText, buttons } = classes;

  const analytics = useAnalytics();

  const { selectedSoftwares, general, professionalInfo, contacts } = professionalPersistSelector ?? {};
  const { longitude, latitude } = location ?? {};

  const { type: pharmacistStepType, validator } = pharmacistStep;
  const { userType: role, language, ...rest } = userProps ?? {};

  const handleSubmit = (values: FormikValues) => {
    const software = softwares?.map(({ softwareId }) =>
      ({
        softwareId,
        expLevel: selectedSoftwares[softwareId] ?? 1,
      }));

    analytics?.logCustomEvent({
      event: GA_EVENTS.signupProgress,
      step_name: SignUpFlowScreens.Contact,
      step_number: step,
    });

    const { pharmacistCapacityId } = professionalInfo ?? {};
    const allData = {
      ...rest,
      ...general,
      ...professionalInfo,
      ...contacts,
      selectedSoftwares: software,
      ...values,
      latitude,
      longitude,
      role,
      preferdLanguage: language,
      prefferedDistanceRange: defaultPrefferedRange,
      [capacityId]: pharmacistCapacityId,
    };
    delete allData.pharmacistCapacityId;
    delete allData.branchNo;
    delete allData.insitutionNo;
    delete allData.accountNo;
    dispatch(createProfessionalProfile(allData));
  };

  const formik = useFormik({
    initialValues: {
      isSelfService: true,
      ...professionalPersistSelector[pharmacistStepType],
      latitude: null,
      longitude: null,
      preferdLanguage: filterLanguage(languages, language ?? Language.fr)?.name,
      role,
    },
    onSubmit: (values) => {
      isLastStep
        ? handleSubmit(values)
        : changeStep(professionalSteps(), values, pharmacistStepType);
    },
    enableReinitialize: true,
    validationSchema: validator,
  });

  const {
    values: formikValues,
    setFieldValue,
    handleSubmit: formikHandleSubmit,
  } = formik ?? {};

  useSignupErrors({ formik });
  const { graduationYear, termsAndConditions } = formikValues ?? {};

  const getMinusCurrentYear = (year: number) =>
    new Date().getFullYear() - year;

  const handleBackButton = () => {
    if (step > 0) {
      setStep((prevStep) =>
        prevStep - 1);
    }
  };

  const buttonLabel = isLastStep ? strings.submit : strings.next;
  const shouldShowSubmit = isLastStep && !termsAndConditions;

  useEffect(() => {
    graduationYear
      && setFieldValue(
        'yearsOfExperience',
        getMinusCurrentYear(parseInt(graduationYear, 10)),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graduationYear]);

  useEffect(() => {
    setFieldValue('termsAndConditions', isTerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerms]);

  return (
    <>
      <Box>
        <ProfileForm selected={pharmacistStepType} formik={formik} isSignup />
      </Box>

      <Box display="flex" flex={1} alignSelf="flex-end" alignItems="flex-end">
        {validStep && (
          <IconButton sx={back} onClick={handleBackButton}>
            <BackIcon />
            <Typography sx={backText}>{strings.back}</Typography>
          </IconButton>
        )}

        <CustomButton
          label={buttonLabel}
          customClass={buttons}
          onClick={formikHandleSubmit}
          disabled={shouldShowSubmit}
        />
      </Box>
    </>
  );
};

export default PharmacistSignupFlow;
